import { useCallback, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import {
  getNativeTokens,
} from "@/integrations/clients/carAuction/nativeAuth";
import { useNativePlatform } from "@/hooks/useNativePlatform";

type AckError = { code: string; message: string; status?: number };
type PlaceBidAckData = {
  highestBid: number;
  time_Extended: boolean;
  new_endTime: string | null;
  total_bids: number;
  currentBuyNowPrice?: number | null;
  canBuyNow?: boolean;
  placedBidAmount?: number;
  isLeading?: boolean;
  outbidByAutoBid?: boolean;
};
type AckResponse =
  | { success: true; data: PlaceBidAckData }
  | { success: false; error: AckError };

export type BidUpdateEvent = {
  bidAmount: number;
  timestamp: number;
  time_Extended?: boolean;
  new_endTime?: string | null;
  currentBuyNowPrice?: number | null;
  canBuyNow?: boolean;
  companyId?: string | null;
  scopeKey?: string;
  isAutoBid?: boolean;
};

export type AutoBidUpdateEvent = {
  isActive: boolean;
  maxAmount: number | null;
  companyId: string | null;
  scopeKey: string;
  reason?: "activated" | "cancelled" | "limit_reached" | "updated" | string;
  timestamp?: number;
};

const SERVER_URL =
  import.meta.env.VITE_API_BASE_URL_SOCKET_IO || "http://localhost:5001";
const SOCKET_PATH = "/socket.io";
const SOCKET_TRANSPORTS = ["websocket"];
const PLACE_BID_TIMEOUT_MS = 15_000;

function buildSocketOptions(isNative: boolean): {
  path: string;
  transports: string[];
  withCredentials: boolean;
  auth?: { token: string };
} {
  const options = {
    path: SOCKET_PATH,
    transports: SOCKET_TRANSPORTS,
    withCredentials: true,
  };

  if (isNative) {
    const accessToken = getNativeTokens()?.accessToken;
    if (accessToken) {
      return { ...options, auth: { token: accessToken } };
    }
  }

  return options;
}

export function useBidSocket(
  listingId?: string,
  auctionId?: string,
  currentBid?: number,
  currentEndTime?: string,
  options?: { enabled?: boolean },
) {
  const { isNative } = useNativePlatform();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [highestBid, setHighestBid] = useState<number | null>(
    currentBid ?? null,
  );
  const [effectiveEndTime, setEffectiveEndTime] = useState<string | null>(
    currentEndTime ?? null,
  );
  const [currentBuyNowPrice, setCurrentBuyNowPrice] = useState<number | null>(
    null,
  );
  const [canBuyNow, setCanBuyNow] = useState<boolean | null>(null);
  const [onBidUpdateCallback, setOnBidUpdateCallback] = useState<
    ((evt: BidUpdateEvent) => void) | null
  >(null);
  const [onAutoBidUpdateCallback, setOnAutoBidUpdateCallback] = useState<
    ((evt: AutoBidUpdateEvent) => void) | null
  >(null);

  useEffect(() => {
    setEffectiveEndTime(currentEndTime ?? null);
  }, [currentEndTime, listingId]);

  useEffect(() => {
    const enabled = options?.enabled !== false;
    if (!enabled) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setIsConnected(false);
      return;
    }

    const socket = io(SERVER_URL, buildSocketOptions(isNative));
    socketRef.current = socket;

    const handleConnect = () => setIsConnected(true);
    const handleDisconnect = () => setIsConnected(false);
    const handleConnectError = () => setIsConnected(false);

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [options?.enabled, isNative]);

  useEffect(() => {
    if (!listingId) return;
    const socket = socketRef.current;
    if (!socket) return;

    const join = () => {
      if (!socket.connected) return;
      socket.emit("joinAuction", listingId, () => {});
    };

    if (socket.connected) {
      join();
    } else {
      socket.once("connect", join);
    }

    return () => {
      if (socket.connected) {
        socket.emit("leaveAuction", listingId, () => {});
      }
    };
  }, [listingId]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const onBidUpdate = (evt: BidUpdateEvent) => {
      setHighestBid(evt.bidAmount);
      if (evt.new_endTime) {
        setEffectiveEndTime(evt.new_endTime);
      }
      if (evt.currentBuyNowPrice != null) {
        setCurrentBuyNowPrice(evt.currentBuyNowPrice);
      }
      if (evt.canBuyNow !== undefined) {
        setCanBuyNow(evt.canBuyNow);
      }
      if (onBidUpdateCallback) {
        onBidUpdateCallback(evt);
      }
    };

    const onAutoBidUpdate = (evt: AutoBidUpdateEvent) => {
      if (onAutoBidUpdateCallback) {
        onAutoBidUpdateCallback(evt);
      }
    };

    socket.on("bidUpdate", onBidUpdate);
    socket.on("autoBidUpdate", onAutoBidUpdate);
    return () => {
      socket.off("bidUpdate", onBidUpdate);
      socket.off("autoBidUpdate", onAutoBidUpdate);
    };
  }, [onBidUpdateCallback, onAutoBidUpdateCallback]);

  const placeBid = useCallback(
    (amount: number): Promise<AckResponse> => {
      return new Promise((resolve, reject) => {
        const socket = socketRef.current;
        if (!socket) {
          reject(new Error("Socket not connected"));
          return;
        }
        if (!socket.connected) {
          reject(new Error("Socket not connected"));
          return;
        }
        if (!listingId || !auctionId) {
          reject(new Error("listingId is required"));
          return;
        }

        let settled = false;
        const timeoutId = window.setTimeout(() => {
          if (settled) return;
          settled = true;
          reject(new Error("Bid request timed out"));
        }, PLACE_BID_TIMEOUT_MS);

        socket.emit(
          "placeBid",
          { listingId, auctionId, amount },
          (res: AckResponse) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);

            if (res?.success) {
              setHighestBid(res.data.highestBid);
              if (res.data.new_endTime) {
                setEffectiveEndTime(res.data.new_endTime);
              }
              if (res.data.currentBuyNowPrice != null) {
                setCurrentBuyNowPrice(res.data.currentBuyNowPrice);
              }
              if (res.data.canBuyNow !== undefined) {
                setCanBuyNow(res.data.canBuyNow);
              }
              resolve(res);
            } else {
              resolve(res);
            }
          },
        );
      });
    },
    [auctionId, listingId],
  );

  const registerBidUpdateCallback = useCallback(
    (callback: (evt: BidUpdateEvent) => void) => {
      setOnBidUpdateCallback(() => callback);
    },
    [],
  );

  const registerAutoBidUpdateCallback = useCallback(
    (callback: (evt: AutoBidUpdateEvent) => void) => {
      setOnAutoBidUpdateCallback(() => callback);
    },
    [],
  );

  return {
    isConnected,
    highestBid,
    effectiveEndTime,
    currentBuyNowPrice,
    canBuyNow,
    placeBid,
    socket: socketRef.current,
    onBidUpdate: registerBidUpdateCallback,
    onAutoBidUpdate: registerAutoBidUpdateCallback,
  };
}
